import { Box, IconButton, TableCell, TableRow } from '@mui/material'
import { Dayjs } from 'dayjs'
import { Services, TimeSheetPageState } from '../../../../../store/slices/timesheet/timesheetPageSlice'
import DeleteIcon from '@mui/icons-material/Delete'
import CustomTimePicker from '../../../../general/CustomTimePicker/CustomTimePicker'
import CustomDatePicker from '../../../../general/CustomDatePicker/CustomDatePicker'

const periodTimesBoxStyle = {
	display: 'flex',
	alignItems: 'center',
	gap: 0.25,
	width: 'fit-content',
}

// Import from types file to avoid circular dependency
export interface WorkTimePeriod {
	// Stable React key; index-based keys cause stale component reuse when a period is removed or reordered.
	_key?: string
	startTime: Dayjs | null | undefined
	isStartTimeValid: boolean
	startDate: Dayjs | null | undefined
	isStartDateValid: boolean
	endTime: Dayjs | null | undefined
	isEndTimeValid: boolean
	endDate: Dayjs | null | undefined
	isEndDateValid: boolean
}

interface WorkPeriodProps {
	isModal?: boolean
	idx: number
	workTime: WorkTimePeriod
	workTimes: WorkTimePeriod[]
	services: Services[]
	timeSheet: TimeSheetPageState
	handleStartTimeChange: (time: Dayjs | null, idx: number) => void
	handleStartDateChange: (date: Dayjs | null, idx: number) => void
	handleEndTimeChange: (time: Dayjs | null, idx: number) => void
	handleEndDateChange: (date: Dayjs | null, idx: number) => void
	handleRemovePeriod: (index: number) => void
}

/**
 * Renders a work period form section for entering and editing start and end times and dates.
 *
 * @param {Object} props - The properties for the component.
 * @param {number} props.idx - The idx of the work period.
 * @param {WorkTimePeriod | null} props.workTime - The work time data for the period.
 * @param {WorkTime[]} props.store - The array of work times.
 * @param {Function} props.handleStartTimeChange - Callback for handling start time changes.
 * @param {Function} props.handleStartDateChange - Callback for handling start date changes.
 * @param {Function} props.handleEndTimeChange - Callback for handling end time changes.
 * @param {Function} props.handleEndDateChange - Callback for handling end date changes.
 * @param {Function} props.canEditPeriod - Function to determine if the period can be edited.
 * @returns {React.JSX.Element} The rendered work period form section.
 */
export function WorkPeriod({
	isModal = false,
	idx,
	workTime,
	workTimes,
	services,
	timeSheet,
	handleStartTimeChange,
	handleStartDateChange,
	handleEndTimeChange,
	handleEndDateChange,
	handleRemovePeriod,
}: Readonly<WorkPeriodProps>): React.JSX.Element {
	return (
		<>
			{!isModal /* Displays only if any of the services mainSecundaryDriver is not null */ &&
				services.some(service => service.mainSecundaryDriver) && (
					<TableRow
						sx={{
							backgroundColor: 'rgba(245, 247, 252, 0.50)',
						}}
					>
						<TableCell
							colSpan={3}
							sx={{
								border: 'none',
								fontFamily: 'var(--font-family)',
								fontSize: '0.75rem',
								borderRight: '1px solid transparent',
								backgroundColor: 'transparent',
								fontWeight: 400,
								lineHeight: '130%',
								letterSpacing: '0.17px',
								color: 'var(--primary-dark)',
							}}
						>
							Intervalo {idx + 1} (Kms em Vazio)
						</TableCell>
						{/* Vehicle fleet number */}
						<TableCell
							colSpan={5}
							sx={{
								border: 'none',
								fontFamily: 'var(--font-family)',
								fontSize: '0.75rem',
								borderRight: '1px solid transparent',
								backgroundColor: 'transparent',
								fontWeight: 400,
								lineHeight: '130%',
								letterSpacing: '0.17px',
								color: 'var(--primary-dark)',
							}}
						>
							{timeSheet.services[0]?.service.vehicle.fleetNumber}
						</TableCell>
					</TableRow>
				)}
			<TableRow
				sx={{
					fontWeight: 400,
					lineHeight: '130%',
					letterSpacing: '0.17px',
					color: 'var(--primary-dark)',
				}}
			>
				{/* Work Period */}
				<TableCell
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '0.75rem',
						fontWeight: 400,
						lineHeight: '130%',
						letterSpacing: '0.17px',
						color: 'var(--primary-dark)',
						borderRight: isModal ? '1px solid rgba(134, 149, 175, 0.15)' : '1px solid transparent',
						backgroundColor: isModal ? 'rgba(245, 247, 252, 0.50)' : 'transparent',
					}}
				>
					{`Período de Trabalho ${idx + 1}`}
				</TableCell>
				<TableCell>
					<Box
						sx={{
							...periodTimesBoxStyle,
						}}
					>
						{/* Period Start Time */}
						<CustomTimePicker
							aria-label={`Hora de início do período ${idx + 1}`}
							value={workTime.startTime}
							onChange={newValue => handleStartTimeChange(newValue, idx)}
						/>
						{/* Period Start Date */}
						<CustomDatePicker
							aria-label={`Data de início do período ${idx + 1}`}
							value={workTime.startDate}
							onChange={newValue => handleStartDateChange(newValue, idx)}
						/>
					</Box>
				</TableCell>
				<TableCell colSpan={services.some(service => service.mainSecundaryDriver) ? 1 : 2}>
					<Box
						sx={{
							...periodTimesBoxStyle,
						}}
					>
						{/* Period End Time */}
						<CustomTimePicker
							aria-label={`Hora de fim do período ${idx + 1}`}
							value={workTime?.endTime}
							onChange={newValue => handleEndTimeChange(newValue, idx)}
						/>
						{/* Period End Date */}
						<CustomDatePicker
							aria-label={`Data de fim do período ${idx + 1}`}
							value={workTime.endDate}
							onChange={newValue => handleEndDateChange(newValue, idx)}
						/>
					</Box>
				</TableCell>
				<TableCell
					colSpan={services.some(service => service.mainSecundaryDriver) ? 5 : 2}
					align='right'
				>
					{idx === workTimes.length - 1 && workTimes.length > 1 && (
						<IconButton
							size='small'
							color='info'
							aria-label='delete period'
							onClick={() => handleRemovePeriod(idx)}
						>
							<DeleteIcon sx={{ width: '16px', height: '16px', color: 'var(--primary)' }} />
						</IconButton>
					)}
				</TableCell>
			</TableRow>
		</>
	)
}
